import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { PRODUCT_STATUS_OPTIONS, RECORDS_PER_PAGE_OPTIONS, NOT_SURE } from '../../../../utils/constants';

import Dropdown from '../../../common/inputs/dropdown/Dropdown';
import Button from '../../../common/button/Button';
import Table from '../../../common/table/Table';
import TabBar from '../../../common/tab-bar/TabBar';
import Checkbox from '../../../common/inputs/checkbox/Checkbox';

import './product-management.scss';

const ProductManagement = ({
  listings,
  initialFilters,
  accounts,
  classifications,
  categories,
  types,
  tab,
  currentPage,
  totalPages,
  changeTab,
  retrieveCategories,
  retrieveTypes,
  retrieveProductListings,
  retrieveBuyerListings,
}) => {
  const [recordsPerPage, setRecordsPerPage] = useState({ label: 20, value: 20 });
  const [status, setStatus] = useState(undefined);
  const [account, setAccount] = useState(undefined);
  const [productClassification, setProductClassification] = useState(undefined);
  const [productCategory, setProductCategory] = useState(undefined);
  const [productType, setProductType] = useState(undefined);
  const [featuredFilter, setFeaturedFilter] = useState(false);

  const navigate = useNavigate();

  const filters = useRef({});

  useEffect(() => {
    setRecordsPerPage(initialFilters.recordsPerPage);
    setStatus(initialFilters.status);
    setAccount(initialFilters.account);
    setProductClassification(initialFilters.productClassification);
    setProductCategory(initialFilters.productCategory);
    setProductType(initialFilters.productType);
    setFeaturedFilter(initialFilters.featuredFilter);

    filters.current = initialFilters;
  }, [initialFilters]);

  const handleTabChange = (selectedTab) => {
    if (tab === selectedTab) return;

    filters.current = {
      ...filters.current,
      page: 0,
    };

    if (selectedTab === 'Product Listings') {
      retrieveProductListings(filters.current);
    } else {
      retrieveBuyerListings(filters.current);
    }

    changeTab(selectedTab);
  };

  const handleClassificationChange = (classification) => {
    if (classification.name === NOT_SURE) {
      setProductClassification(classification);
      setProductCategory(null);
      setProductType(null);
    } else {
      setProductClassification(classification);
      setProductCategory(null);
      setProductType(null);
      retrieveCategories(classification.externalReference);
    }
  };

  const handleCategoryChange = (category) => {
    if (category.name === NOT_SURE) {
      setProductCategory(category);
      setProductType(null);
    } else {
      setProductCategory(category);
      setProductType(null);
      retrieveTypes(category.externalReference);
    }
  };

  const handleTypeChange = (type) => {
    setProductType(type);
  };

  const handleRowClick = (listing) => {
    if (tab === 'Product Listings') {
      navigate(`/admin/product-management/product-listing/${listing.externalReference}`);
    } else {
      navigate(`/admin/product-management/buyer-listing/${listing.externalReference}`);
    }
  };

  const haveFiltersChanged = () => {
    let definedKeys = 0;
    for (let key in filters.current) {
      if (filters.current[key] !== undefined && filters.current[key] !== null && filters.current[key] !== '') {
        definedKeys++;
      }
    }

    if (filters.current.page !== 0 || filters.current.recordsPerPage.value !== 20 || definedKeys !== 2) {
      return true;
    }

    return false;
  };

  const handleFeaturedChange = () => {
    setFeaturedFilter(!featuredFilter);
  };

  const applyFilters = (page) => {
    filters.current = {
      productClassification,
      productCategory,
      productType,
      recordsPerPage,
      account,
      status,
      page,
      featuredFilter,
    };

    if (tab === 'Product Listings') {
      retrieveProductListings(filters.current);
    } else {
      retrieveBuyerListings(filters.current);
    }
  };

  const removeFilters = () => {
    const recordsPerPage = RECORDS_PER_PAGE_OPTIONS[1];

    filters.current = {
      page: 0,
      recordsPerPage,
    };

    setRecordsPerPage(recordsPerPage);
    setStatus(undefined);
    setAccount(undefined);
    setProductClassification(undefined);
    setProductCategory(undefined);
    setProductType(undefined);
    setFeaturedFilter(false);

    if (tab === 'Product Listings') {
      retrieveProductListings(filters.current);
    } else {
      retrieveBuyerListings(filters.current);
    }
  };

  return (
    <section className="product-management" data-testid="product-management">
      <section className="product-management__header">
        <TabBar
          className="product-management__tabs"
          tab={tab}
          tabs={['Product Listings', 'Buyer Listings']}
          onTabChange={handleTabChange}
        />
      </section>
      <section className="product-management__container">
        <section className="product-management__filter" data-testid="product-management-filters">
          <section className="product-management__filter__filters">
            <p>Filter</p>

            <label htmlFor="records-per-page">Records per Page</label>
            <Dropdown
              name="records-per-page"
              value={recordsPerPage}
              options={RECORDS_PER_PAGE_OPTIONS}
              onChange={(option) => setRecordsPerPage(option)}
            />

            <label htmlFor="product-status">Status</label>
            <Dropdown
              name="product-status"
              value={status}
              options={PRODUCT_STATUS_OPTIONS}
              onChange={(option) => setStatus(option)}
            />

            <label htmlFor="account">Account</label>
            <Dropdown
              name="account"
              value={account}
              options={accounts}
              onChange={(option) => setAccount(option)}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
            />

            <label htmlFor="product-classification">Product Classification</label>
            <Dropdown
              name="product-classification"
              value={productClassification}
              options={classifications}
              onChange={handleClassificationChange}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              noSort
            />

            <label htmlFor="product-category">Product Category</label>
            <Dropdown
              name="product-category"
              value={productCategory}
              options={categories}
              onChange={handleCategoryChange}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              disabled={!productClassification || productClassification?.externalReference === NOT_SURE}
              noSort
            />

            <label htmlFor="product-type">Product Type</label>
            <Dropdown
              name="product-type"
              value={productType}
              options={types}
              onChange={handleTypeChange}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              disabled={!productCategory}
              noSort
            />
            <div className="product-management__featured-filter">
              <label>Featured Listings</label>
              <div>
                <Checkbox value={featuredFilter} onChange={handleFeaturedChange} />
              </div>
            </div>
          </section>

          <div className="product-management__filter__button-container">
            <Button onClick={() => applyFilters(0)}>Apply Filter</Button>
            {haveFiltersChanged() && <Button onClick={() => removeFilters()}>Remove Filter</Button>}
          </div>
        </section>

        <section className="product-management__table">
          <section className="product-management__table__header">
            <h1>{tab}</h1>
          </section>

          <section className="product-management__table__container">
            <Table
              data={listings}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={applyFilters}
              onRowClick={handleRowClick}
              emptyMessage={`No ${tab === 'Product Listings' ? 'product' : 'buyer'} listings found`}
              hiddenColumns={['externalReference']}
            />
          </section>
        </section>
      </section>
    </section>
  );
};

ProductManagement.propTypes = {
  listings: PropTypes.arrayOf(PropTypes.shape({})),
  initialFilters: PropTypes.shape({}).isRequired,
  accounts: PropTypes.arrayOf(PropTypes.shape({})),
  classifications: PropTypes.arrayOf(PropTypes.shape({})),
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  types: PropTypes.arrayOf(PropTypes.shape({})),
  tab: PropTypes.string.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  retrieveCategories: PropTypes.func.isRequired,
  retrieveTypes: PropTypes.func.isRequired,
  retrieveProductListings: PropTypes.func.isRequired,
  retrieveBuyerListings: PropTypes.func.isRequired,
};

ProductManagement.defaultProps = {
  listings: [],
  accounts: [],
  classifications: [],
  categories: [],
  types: [],
};

export default ProductManagement;
